import { useEffect, useState } from 'react';
import useInboxStore from '@/stores/InboxStore';
import useWorkflowStore from '@/stores/WorkflowStore';
import {
  Check,
  Clock,
  ExternalLink,
  Linkedin,
  Loader2,
  MessageCircle,
  MessagesSquare,
  RefreshCw,
  Search,
  Tag,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import { channelCapabilities, getChannelInfo } from '@/lib/channelCapabilities';
import { fetchLinkedInPosts } from '@/hooks/workflow';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

const TriggerConfiguration = ({
  isTriggerModalOpen,
  setIsTriggerModalOpen,
  isConfigSheetOpen,
  setIsConfigSheetOpen,
  selectedTriggerNode,
  setSelectedTriggerNode,
  setIsActionTypeModalOpen,
  setActionSourceNode,
  nodes,
  setNodes,
  edges,
  setEdges,
  runLayout,
}) => {
  // --- State Definitions ---
  const [messageText, setMessageText] = useState('');
  const [messageTexts, setMessageTexts] = useState([]);
  const [messageWords, setMessageWords] = useState([]);
  const [messageOperator, setMessageOperator] = useState('equals');
  const [commentText, setCommentText] = useState('');
  const [commentTexts, setCommentTexts] = useState([]);
  const [commentWords, setCommentWords] = useState([]);
  const [commentOperator, setCommentOperator] = useState('equals');
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagOperator, setTagOperator] = useState('equals');
  const [tempText, setTempText] = useState('');
  const [tempCommentText, setTempCommentText] = useState('');
  const [tempWord, setTempWord] = useState('');
  const [tempCommentWord, setTempCommentWord] = useState('');
  const [currentTriggerType, setCurrentTriggerType] = useState(null);

  // Schedule trigger state variables
  const [scheduleUnit, setScheduleUnit] = useState('minutes');
  const [scheduleValue, setScheduleValue] = useState(30);
  const [scheduleConditions, setScheduleConditions] = useState([]);
  const [selectedConditionField, setSelectedConditionField] = useState('');
  const [selectedConditionOperator, setSelectedConditionOperator] =
    useState('');
  const [conditionTagIds, setConditionTagIds] = useState([]);
  const [conditionTimestampOperator, setConditionTimestampOperator] =
    useState('gt');
  const [conditionTimestampUnit, setConditionTimestampUnit] =
    useState('minutes');
  const [conditionTimestampValue, setConditionTimestampValue] = useState(30);
  const [conditionMessageDirection, setConditionMessageDirection] =
    useState('sent');

  // LinkedIn Post Comment trigger state variables
  const [linkedinPosts, setLinkedinPosts] = useState([]);
  const [linkedinPostsLoading, setLinkedinPostsLoading] = useState(false);
  const [linkedinSelectedPostIds, setLinkedinSelectedPostIds] = useState([]);
  const [linkedinOperator, setLinkedinOperator] = useState('word_in');
  const [linkedinCommentText, setLinkedinCommentText] = useState('');
  const [linkedinCommentTexts, setLinkedinCommentTexts] = useState([]);
  const [linkedinCommentWords, setLinkedinCommentWords] = useState([]);
  const [tempLinkedinWord, setTempLinkedinWord] = useState('');
  const [tempLinkedinText, setTempLinkedinText] = useState('');
  const [linkedinSearchQuery, setLinkedinSearchQuery] = useState('');
  const [linkedinSearchInput, setLinkedinSearchInput] = useState('');

  const tags = useInboxStore(
    (state) =>
      state.tags.filter(
        (tag) =>
          // Filter out special system tags that aren't actual tags
          !['No-Tag-SBCCRM', 'Seen-Not-Responded', 'Not-Responded'].includes(
            tag._id
          )
      ) || []
  );

  const setTempTrigger = useWorkflowStore((state) => state.setTempTrigger);

  // Condition field options for schedule triggers
  const conditionFieldOptions = [
    {
      value: 'contact_tag',
      label: 'Contact Tag',
      description: 'Check if contact has specific tags',
    },
    {
      value: 'contact.latest_message_received_timestamp',
      label: 'Time Since Last Message Received',
      description: 'Check time since last message was received',
    },
    {
      value: 'contact.latest_message_sent_timestamp',
      label: 'Time Since Last Message Sent',
      description: 'Check time since last message was sent',
    },
    {
      value: 'contact.latest_message_direction',
      label: 'Last Message Direction',
      description: 'Check direction of last message',
    },
  ];

  // Helper function to add a new condition
  const addCondition = () => {
    if (!selectedConditionField || !selectedConditionOperator) {
      toast.error('Please select both field and operator');
      return;
    }

    // Allow multiple conditions of the same field type
    // No restriction on adding the same field multiple times

    let conditionValue = '';

    if (selectedConditionField === 'contact_tag') {
      if (conditionTagIds.length === 0) {
        toast.error('Please select at least one tag');
        return;
      }
      conditionValue = conditionTagIds;
    } else if (
      selectedConditionField === 'contact.latest_message_received_timestamp'
    ) {
      const numValue = Number(conditionTimestampValue);
      if (!conditionTimestampValue || isNaN(numValue) || numValue <= 0) {
        toast.error('Please enter a valid timestamp value');
        return;
      }
      conditionValue = {
        operator: conditionTimestampOperator,
        unit: conditionTimestampUnit,
        unit_value: numValue,
      };
    } else if (
      selectedConditionField === 'contact.latest_message_sent_timestamp'
    ) {
      const numValue = Number(conditionTimestampValue);
      if (!conditionTimestampValue || isNaN(numValue) || numValue <= 0) {
        toast.error('Please enter a valid timestamp value');
        return;
      }
      conditionValue = {
        operator: conditionTimestampOperator,
        unit: conditionTimestampUnit,
        unit_value: numValue,
      };
    } else if (selectedConditionField === 'contact.latest_message_direction') {
      if (!conditionMessageDirection) {
        toast.error('Please select message direction');
        return;
      }
      conditionValue = conditionMessageDirection;
    }

    const newCondition = {
      type: 'condition',
      field: selectedConditionField,
      operator: selectedConditionOperator,
      value: conditionValue,
    };

    setScheduleConditions([...scheduleConditions, newCondition]);

    // Reset form fields
    setSelectedConditionField('');
    setSelectedConditionOperator('');
    setConditionTagIds([]);
    setConditionTimestampOperator('gt');
    setConditionTimestampUnit('minutes');
    setConditionTimestampValue(30);
    setConditionMessageDirection('sent');
  };

  // Helper function to remove a condition
  const removeCondition = (index) => {
    setScheduleConditions(scheduleConditions.filter((_, i) => i !== index));
  };

  const triggerOptions = [
    {
      type: 'any_message',
      label: 'Any Message',
      icon: MessagesSquare,
      description: 'Trigger whenever any message is received',
      bgClass: 'bg-green-500/10',
      borderClass: 'border-green-500/20',
      iconClass: 'text-green-500',
    },
    {
      type: 'message_text',
      label: 'Message Text',
      icon: MessagesSquare,
      description: 'Trigger when a specific message is received',
      bgClass: 'bg-primary/10',
      borderClass: 'border-primary/20',
      iconClass: 'text-primary',
    },
    {
      type: 'comment_text',
      label: 'Comment Text',
      icon: MessageCircle,
      description: 'Trigger when a specific comment is received',
      bgClass: 'bg-primary/10',
      borderClass: 'border-primary/20',
      iconClass: 'text-primary',
    },
    {
      type: 'contact_tag',
      label: 'Contact Tag',
      icon: Tag,
      description: 'Trigger when a contact is tagged',
      bgClass: 'bg-secondary/20',
      borderClass: 'border-secondary/30',
      iconClass: 'text-secondary-foreground',
    },
    {
      type: 'schedule',
      label: 'Schedule',
      icon: Clock,
      description: 'Trigger based on time intervals and conditions',
      bgClass: 'bg-orange-500/10',
      borderClass: 'border-orange-500/20',
      iconClass: 'text-orange-500',
    },
    {
      type: 'linkedin_post_comment',
      label: 'LinkedIn Post Comment',
      icon: Linkedin,
      description: 'Trigger when a comment is received on a LinkedIn post',
      bgClass: 'bg-blue-600/10',
      borderClass: 'border-blue-600/20',
      iconClass: 'text-blue-600',
    },
  ];

  const handleTriggerTypeSelection = (type) => {
    setCurrentTriggerType(type);
    setIsTriggerModalOpen(false);
    setIsConfigSheetOpen(true);
  };

  // Add a text value to the list of texts
  const addTextToList = () => {
    if (tempText.trim()) {
      setMessageTexts([...messageTexts, tempText.trim()]);
      setTempText('');
    }
  };

  // Add a comment text value to the list of comment texts
  const addCommentTextToList = () => {
    if (tempCommentText.trim()) {
      setCommentTexts([...commentTexts, tempCommentText.trim()]);
      setTempCommentText('');
    }
  };

  // Add a word to the list of words
  const addWordToList = () => {
    if (tempWord.trim()) {
      setMessageWords([...messageWords, tempWord.trim()]);
      setTempWord('');
    }
  };

  // Add a comment word to the list of comment words
  const addCommentWordToList = () => {
    if (tempCommentWord.trim()) {
      setCommentWords([...commentWords, tempCommentWord.trim()]);
      setTempCommentWord('');
    }
  };

  // Remove a text from the list
  const removeTextFromList = (index) => {
    setMessageTexts(messageTexts.filter((_, i) => i !== index));
  };

  // Remove a comment text from the list
  const removeCommentTextFromList = (index) => {
    setCommentTexts(commentTexts.filter((_, i) => i !== index));
  };

  // Remove a word from the list
  const removeWordFromList = (index) => {
    setMessageWords(messageWords.filter((_, i) => i !== index));
  };

  // Remove a comment word from the list
  const removeCommentWordFromList = (index) => {
    setCommentWords(commentWords.filter((_, i) => i !== index));
  };

  // Handle key press for adding text
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTextToList();
    }
  };

  // Handle key press for adding comment text
  const handleCommentKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCommentTextToList();
    }
  };

  // Handle key press for adding word
  const handleWordKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addWordToList();
    }
  };

  // Handle key press for adding comment word
  const handleCommentWordKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCommentWordToList();
    }
  };

  // LinkedIn Post Comment trigger helper functions
  const loadLinkedInPosts = async (searchQuery = '', force = false) => {
    setLinkedinPostsLoading(true);
    try {
      const params = {};
      if (searchQuery) params.q = searchQuery;
      if (force) params.force = true;

      const response = await fetchLinkedInPosts(params);
      setLinkedinPosts(response?.data || []);
    } catch (error) {
      console.error('Error fetching LinkedIn posts:', error);
      toast.error('Failed to load LinkedIn posts');
    } finally {
      setLinkedinPostsLoading(false);
    }
  };

  const handleLinkedinSearch = () => {
    setLinkedinSearchQuery(linkedinSearchInput);
    loadLinkedInPosts(linkedinSearchInput);
  };

  const handleLinkedinSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLinkedinSearch();
    }
  };

  const handleLinkedinRefresh = () => {
    loadLinkedInPosts(linkedinSearchQuery, true);
  };

  const toggleLinkedinPostSelection = (postId) => {
    setLinkedinSelectedPostIds((prev) => {
      if (prev.includes(postId)) {
        return prev.filter((id) => id !== postId);
      } else {
        return [...prev, postId];
      }
    });
  };

  const addLinkedinWordToList = () => {
    if (tempLinkedinWord.trim()) {
      setLinkedinCommentWords([
        ...linkedinCommentWords,
        tempLinkedinWord.trim(),
      ]);
      setTempLinkedinWord('');
    }
  };

  const removeLinkedinWordFromList = (index) => {
    setLinkedinCommentWords(linkedinCommentWords.filter((_, i) => i !== index));
  };

  const addLinkedinTextToList = () => {
    if (tempLinkedinText.trim()) {
      setLinkedinCommentTexts([
        ...linkedinCommentTexts,
        tempLinkedinText.trim(),
      ]);
      setTempLinkedinText('');
    }
  };

  const removeLinkedinTextFromList = (index) => {
    setLinkedinCommentTexts(linkedinCommentTexts.filter((_, i) => i !== index));
  };

  const handleLinkedinWordKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addLinkedinWordToList();
    }
  };

  const handleLinkedinTextKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addLinkedinTextToList();
    }
  };

  const handleChangeTriggerType = () => {
    setIsConfigSheetOpen(false);
    setIsTriggerModalOpen(true);
  };

  // --- Effects ---
  useEffect(() => {
    if (selectedTriggerNode) {
      setCurrentTriggerType(selectedTriggerNode.data.triggerType);
      if (selectedTriggerNode.data.triggerType) {
        // Open config sheet directly if already configured
        setCurrentTriggerType(selectedTriggerNode.data.triggerType);
        // Load existing config...
        if (selectedTriggerNode.data.triggerType === 'message_text') {
          setMessageOperator(
            selectedTriggerNode.data.config?.operator || 'equals'
          );
          if (selectedTriggerNode.data.config?.operator === 'equals') {
            setMessageText(selectedTriggerNode.data.config?.text || '');
            setMessageTexts([]); // Clear list if switching to equals
            setMessageWords([]); // Clear words if switching to equals
          } else if (selectedTriggerNode.data.config?.operator === 'in') {
            setMessageTexts(selectedTriggerNode.data.config?.texts || []);
            setMessageText(''); // Clear single text if switching to in
            setMessageWords([]); // Clear words if switching to in
          } else if (selectedTriggerNode.data.config?.operator === 'word_in') {
            setMessageWords(selectedTriggerNode.data.config?.words || []);
            setMessageText(''); // Clear single text if switching to word_in
            setMessageTexts([]); // Clear texts if switching to word_in
          }
        } else if (selectedTriggerNode.data.triggerType === 'comment_text') {
          setCommentOperator(
            selectedTriggerNode.data.config?.operator || 'equals'
          );
          if (selectedTriggerNode.data.config?.operator === 'equals') {
            setCommentText(selectedTriggerNode.data.config?.text || '');
            setCommentTexts([]); // Clear list if switching to equals
            setCommentWords([]); // Clear words if switching to equals
          } else if (selectedTriggerNode.data.config?.operator === 'in') {
            setCommentTexts(selectedTriggerNode.data.config?.texts || []);
            setCommentText(''); // Clear single text if switching to in
            setCommentWords([]); // Clear words if switching to in
          } else if (selectedTriggerNode.data.config?.operator === 'word_in') {
            setCommentWords(selectedTriggerNode.data.config?.words || []);
            setCommentText(''); // Clear single text if switching to word_in
            setCommentTexts([]); // Clear texts if switching to word_in
          }
        } else if (selectedTriggerNode.data.triggerType === 'contact_tag') {
          setTagOperator(selectedTriggerNode.data.config?.operator || 'equals');
          setSelectedTags(selectedTriggerNode.data.config?.tags || []);
        } else if (selectedTriggerNode.data.triggerType === 'schedule') {
          console.log(
            'selectedTriggerNode.data.config',
            selectedTriggerNode.data.config
          );
          // Load schedule configuration
          if (selectedTriggerNode.data.config?.value) {
            setScheduleUnit(
              selectedTriggerNode.data.config.value.unit || 'minutes'
            );
            setScheduleValue(selectedTriggerNode.data.config.value.value || 30);
          }
          if (selectedTriggerNode.data.config?.conditions) {
            setScheduleConditions(selectedTriggerNode.data.config.conditions);
          }
        } else if (
          selectedTriggerNode.data.triggerType === 'linkedin_post_comment'
        ) {
          // Load LinkedIn post comment configuration
          setLinkedinOperator(
            selectedTriggerNode.data.config?.operator || 'word_in'
          );
          if (selectedTriggerNode.data.config?.operator === 'equals') {
            setLinkedinCommentText(selectedTriggerNode.data.config?.text || '');
            setLinkedinCommentTexts([]);
            setLinkedinCommentWords([]);
          } else if (selectedTriggerNode.data.config?.operator === 'in') {
            setLinkedinCommentTexts(
              selectedTriggerNode.data.config?.texts || []
            );
            setLinkedinCommentText('');
            setLinkedinCommentWords([]);
          } else if (selectedTriggerNode.data.config?.operator === 'word_in') {
            setLinkedinCommentWords(
              selectedTriggerNode.data.config?.words || []
            );
            setLinkedinCommentText('');
            setLinkedinCommentTexts([]);
          }
          // Load post selection - support both old single post_id and new post_ids array
          const postIds =
            selectedTriggerNode.data.config?.post_ids ||
            (selectedTriggerNode.data.config?.post_id
              ? [selectedTriggerNode.data.config.post_id]
              : []);
          setLinkedinSelectedPostIds(postIds);
          // Load posts for selection
          loadLinkedInPosts();
        }
        setIsConfigSheetOpen(true);
      }
    }
  }, [selectedTriggerNode, setCurrentTriggerType, setIsConfigSheetOpen]);

  // Load LinkedIn posts when LinkedIn trigger type is selected
  useEffect(() => {
    if (
      currentTriggerType === 'linkedin_post_comment' &&
      linkedinPosts.length === 0
    ) {
      loadLinkedInPosts();
    }
  }, [currentTriggerType]);

  const handleSaveConfig = () => {
    let config = {};
    let isValid = false;
    let triggerOption = null;

    // Find the trigger option details
    triggerOption = triggerOptions.find(
      (opt) => opt.type === currentTriggerType
    );
    if (!triggerOption) return; // Should not happen

    // Build config and validate based on type
    if (currentTriggerType === 'any_message') {
      isValid = true; // Any message trigger doesn't need additional configuration
      config = { operator: 'any', value: null };
    } else if (currentTriggerType === 'message_text') {
      if (messageOperator === 'equals') {
        isValid = messageText.trim() !== '';
        config = { operator: messageOperator, text: messageText };
      } else if (messageOperator === 'in') {
        isValid =
          messageTexts.length > 0 && messageTexts.every((t) => t.trim() !== '');
        config = {
          operator: messageOperator,
          texts: messageTexts.map((t) => t.trim()).filter(Boolean),
        };
      } else if (messageOperator === 'word_in') {
        isValid =
          messageWords.length > 0 && messageWords.every((w) => w.trim() !== '');
        config = {
          operator: messageOperator,
          words: messageWords.map((w) => w.trim()).filter(Boolean),
        };
      }
    } else if (currentTriggerType === 'comment_text') {
      if (commentOperator === 'equals') {
        isValid = commentText.trim() !== '';
        config = { operator: commentOperator, text: commentText };
      } else if (commentOperator === 'in') {
        isValid =
          commentTexts.length > 0 && commentTexts.every((t) => t.trim() !== '');
        config = {
          operator: commentOperator,
          texts: commentTexts.map((t) => t.trim()).filter(Boolean),
        };
      } else if (commentOperator === 'word_in') {
        isValid =
          commentWords.length > 0 && commentWords.every((w) => w.trim() !== '');
        config = {
          operator: commentOperator,
          words: commentWords.map((w) => w.trim()).filter(Boolean),
        };
      }
    } else if (currentTriggerType === 'contact_tag') {
      isValid = selectedTags.length > 0;
      if (isValid) {
        const tagObjects = selectedTags
          .map((tagId) => {
            const tagObj = tags.find((t) => t._id === tagId);
            return tagObj
              ? { id: tagId, name: tagObj.name, colour: tagObj.colour }
              : null; // Exclude tags not found in store
          })
          .filter(Boolean); // Ensure only valid tag objects are included
        config = { operator: tagOperator, tags: selectedTags, tagObjects };
      }
    } else if (currentTriggerType === 'schedule') {
      const numScheduleValue = Number(scheduleValue);
      isValid =
        scheduleConditions.length > 0 &&
        scheduleUnit &&
        scheduleValue &&
        !isNaN(numScheduleValue) &&
        ((scheduleUnit === 'minutes' && numScheduleValue >= 30) ||
          (scheduleUnit === 'hours' && numScheduleValue >= 1) ||
          (scheduleUnit === 'days' && numScheduleValue >= 1));
      if (isValid) {
        // Process conditions to include additional data for display
        const processedConditions = scheduleConditions.map((condition) => {
          if (condition.field === 'contact_tag') {
            const tagObjects = condition.value
              .map((tagId) => {
                const tagObj = tags.find((t) => t._id === tagId);
                return tagObj
                  ? { id: tagId, name: tagObj.name, colour: tagObj.colour }
                  : null;
              })
              .filter(Boolean);
            return { ...condition, tagObjects };
          }
          return condition;
        });

        config = {
          operator: 'interval',
          value: {
            unit: scheduleUnit,
            value: Number(scheduleValue),
          },
          conditions: processedConditions,
        };
      }
    } else if (currentTriggerType === 'linkedin_post_comment') {
      // Validate based on operator
      if (linkedinOperator === 'equals') {
        isValid = linkedinCommentText.trim() !== '';
        config = {
          operator: linkedinOperator,
          text: linkedinCommentText.trim(),
        };
      } else if (linkedinOperator === 'in') {
        isValid =
          linkedinCommentTexts.length > 0 &&
          linkedinCommentTexts.every((t) => t.trim() !== '');
        config = {
          operator: linkedinOperator,
          texts: linkedinCommentTexts.map((t) => t.trim()).filter(Boolean),
        };
      } else if (linkedinOperator === 'word_in') {
        isValid =
          linkedinCommentWords.length > 0 &&
          linkedinCommentWords.every((w) => w.trim() !== '');
        config = {
          operator: linkedinOperator,
          words: linkedinCommentWords.map((w) => w.trim()).filter(Boolean),
        };
      }

      // Require at least one post to be selected
      if (linkedinSelectedPostIds.length === 0) {
        isValid = false;
      } else {
        config.post_ids = linkedinSelectedPostIds;
        // Find post details for display
        const selectedPosts = linkedinPosts.filter((p) =>
          linkedinSelectedPostIds.includes(p.post_id)
        );
        if (selectedPosts.length > 0) {
          config.postDetails = selectedPosts.map((post) => ({
            id: post.id,
            post_id: post.post_id,
            title: post.title,
            share_url: post.share_url,
          }));
        }
      }
    }

    if (!isValid) {
      // Show specific error message based on trigger type
      let errorMessage = 'Please complete the trigger configuration.';

      if (currentTriggerType === 'message_text') {
        if (messageOperator === 'equals') {
          errorMessage = 'Please enter the message text to match.';
        } else if (messageOperator === 'in') {
          errorMessage = 'Please add at least one message text.';
        } else if (messageOperator === 'word_in') {
          errorMessage = 'Please add at least one keyword.';
        }
      } else if (currentTriggerType === 'comment_text') {
        if (commentOperator === 'equals') {
          errorMessage = 'Please enter the comment text to match.';
        } else if (commentOperator === 'in') {
          errorMessage = 'Please add at least one comment text.';
        } else if (commentOperator === 'word_in') {
          errorMessage = 'Please add at least one keyword.';
        }
      } else if (currentTriggerType === 'contact_tag') {
        errorMessage = 'Please select at least one tag.';
      } else if (currentTriggerType === 'schedule') {
        const numScheduleValue = Number(scheduleValue);
        if (scheduleConditions.length === 0) {
          errorMessage =
            'Please add at least one condition for the schedule trigger.';
        } else if (
          (scheduleUnit === 'minutes' && numScheduleValue < 30) ||
          (scheduleUnit === 'hours' && numScheduleValue < 1) ||
          (scheduleUnit === 'days' && numScheduleValue < 1)
        ) {
          errorMessage =
            'Schedule interval must be at least 30 minutes, 1 hour, or 1 day.';
        }
      } else if (currentTriggerType === 'linkedin_post_comment') {
        if (linkedinSelectedPostIds.length === 0) {
          errorMessage = 'Please select at least one LinkedIn post to monitor.';
        } else if (
          linkedinOperator === 'equals' &&
          !linkedinCommentText.trim()
        ) {
          errorMessage = 'Please enter the comment text to match.';
        } else if (
          linkedinOperator === 'in' &&
          linkedinCommentTexts.length === 0
        ) {
          errorMessage = 'Please add at least one comment text.';
        } else if (
          linkedinOperator === 'word_in' &&
          linkedinCommentWords.length === 0
        ) {
          errorMessage = 'Please add at least one keyword.';
        }
      }

      toast.error(errorMessage);
      return;
    }

    // Save to workflow store in expected format
    if (currentTriggerType === 'any_message') {
      setTempTrigger([
        {
          type: 'any_message',
          operator: 'any',
          value: null,
        },
      ]);
    } else if (currentTriggerType === 'message_text') {
      setTempTrigger([
        {
          type: 'message_text',
          operator: messageOperator,
          value:
            messageOperator === 'equals'
              ? messageText.trim()
              : messageOperator === 'word_in'
                ? messageWords.map((w) => w.trim()).filter(Boolean)
                : messageTexts.map((t) => t.trim()).filter(Boolean),
        },
      ]);
    } else if (currentTriggerType === 'comment_text') {
      setTempTrigger([
        {
          type: 'comment_text',
          operator: commentOperator,
          value:
            commentOperator === 'equals'
              ? commentText.trim()
              : commentOperator === 'word_in'
                ? commentWords.map((w) => w.trim()).filter(Boolean)
                : commentTexts.map((t) => t.trim()).filter(Boolean),
        },
      ]);
    } else if (currentTriggerType === 'contact_tag') {
      setTempTrigger([
        {
          type: 'contact_tag',
          operator: tagOperator,
          value:
            tagOperator === 'equals' ? selectedTags[0] || '' : selectedTags,
        },
      ]);
    } else if (currentTriggerType === 'schedule') {
      setTempTrigger([
        {
          type: 'schedule',
          operator: 'interval',
          value: {
            unit: scheduleUnit,
            value: Number(scheduleValue),
          },
          conditions: scheduleConditions.map((condition) => {
            if (condition.field === 'contact_tag') {
              console.log('scheduleConditions', scheduleConditions);
              return {
                type: condition.type,
                field: condition.field,
                operator: condition.operator,
                value:
                  condition.operator === 'equals'
                    ? condition.value[0]
                    : condition.value,
              };
            }
            return condition;
          }),
        },
      ]);
    } else if (currentTriggerType === 'linkedin_post_comment') {
      const triggerData = {
        type: 'linkedin_post_comment',
        operator: linkedinOperator,
        value:
          linkedinOperator === 'equals'
            ? linkedinCommentText.trim()
            : linkedinOperator === 'word_in'
              ? linkedinCommentWords.map((w) => w.trim()).filter(Boolean)
              : linkedinCommentTexts.map((t) => t.trim()).filter(Boolean),
        post_ids: linkedinSelectedPostIds,
      };

      setTempTrigger([triggerData]);
    }

    // --- Update the trigger node ---
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === selectedTriggerNode.id) {
          return {
            ...node,
            data: {
              ...node.data,
              label: triggerOption.label,
              description: triggerOption.description,
              bgClass: triggerOption.bgClass,
              borderClass: triggerOption.borderClass,
              iconClass: triggerOption.iconClass,
              icon: triggerOption.icon,
              triggerType: currentTriggerType,
              config,
            },
          };
        }
        return node;
      })
    );

    // --- Ensure END node and edge exist ---

    const triggerNode = nodes.find((n) => n.id === selectedTriggerNode.id);
    if (!triggerNode) {
      console.error('saveNodeConfig: Trigger node not found after update.');
      return; // Should exist
    }

    const hasOutgoingEdge = edges.some(
      (e) => e.source === selectedTriggerNode.id
    );

    if (!hasOutgoingEdge) {
      let endNodeId = nodes.find(
        (n) => n.type === 'end' && !edges.some((e) => e.target === n.id)
      )?.id; // Find an unconnected end node if possible

      // If no suitable end node exists, create one
      if (!endNodeId) {
        endNodeId = `end-${uuidv4()}`;

        const newEndNode = {
          id: endNodeId,
          type: 'end',
          position: {
            x: triggerNode.position.x, // Align horizontally initially
            y: triggerNode.position.y + 250, // Place below initially
          },
          data: { label: 'END' },
        };

        // **Add the End node to state FIRST**
        setNodes((nds) => {
          // Avoid adding duplicates if it somehow already exists
          if (!nds.some((n) => n.id === endNodeId)) {
            return [...nds, newEndNode];
          }
          return nds;
        });
      }

      // **Add the edge to state AFTER the End node is potentially added**
      setEdges((eds) => {
        // Avoid adding duplicate edges
        if (
          !eds.some(
            (e) => e.source === selectedTriggerNode.id && e.target === endNodeId
          )
        ) {
          return [
            ...eds,
            {
              id: `e-${selectedTriggerNode.id}-${endNodeId}`,
              source: selectedTriggerNode.id,
              target: endNodeId,
              type: 'custom',
              className: 'stroke-slate-300 stroke-[1.5px]',
              data: {
                showAddButton: true,
                allowBranchStep: false,
                setIsActionTypeModalOpen,
                setActionSourceNode,
              },
            },
          ];
        }
        return eds;
      });
    }

    setIsConfigSheetOpen(false);
    setSelectedTriggerNode(null); // Reset current node ID

    runLayout();
  };

  return (
    <>
      <Sheet open={isConfigSheetOpen} onOpenChange={setIsConfigSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <div className='flex items-center justify-between'>
              <SheetTitle>
                {currentTriggerType === 'any_message'
                  ? 'Any Message Trigger'
                  : currentTriggerType === 'message_text'
                    ? 'Message Text Trigger'
                    : currentTriggerType === 'comment_text'
                      ? 'Comment Text Trigger'
                      : currentTriggerType === 'contact_tag'
                        ? 'Contact Tag Trigger'
                        : currentTriggerType === 'schedule'
                          ? 'Schedule Trigger'
                          : currentTriggerType === 'linkedin_post_comment'
                            ? 'LinkedIn Post Comment Trigger'
                            : 'Configure Trigger'}
              </SheetTitle>
              <Button
                variant='outline'
                size='sm'
                onClick={handleChangeTriggerType}
                className='ml-auto'
              >
                Change Trigger Type
              </Button>
            </div>
            <SheetDescription>
              {currentTriggerType === 'any_message'
                ? 'This trigger will activate whenever any message is received, regardless of content.'
                : currentTriggerType === 'message_text'
                  ? 'Configure when this trigger should activate based on message text.'
                  : currentTriggerType === 'comment_text'
                    ? 'Configure when this trigger should activate based on comment text.'
                    : currentTriggerType === 'contact_tag'
                      ? 'Configure when this trigger should activate based on contact tags.'
                      : currentTriggerType === 'schedule'
                        ? 'Configure when this trigger should activate based on time intervals and conditions.'
                        : currentTriggerType === 'linkedin_post_comment'
                          ? 'Configure when this trigger should activate based on comments on your LinkedIn posts.'
                          : 'Configure this trigger.'}
            </SheetDescription>
          </SheetHeader>

          <div className='mt-6 space-y-6'>
            {currentTriggerType === 'any_message' && <></>}
            {currentTriggerType === 'message_text' && (
              <>
                <div className='space-y-2'>
                  <span className='text-sm font-medium'>Select Operator</span>
                  <Select
                    value={messageOperator}
                    onValueChange={setMessageOperator}
                  >
                    <SelectTrigger className='w-full'>
                      <SelectValue placeholder='Select an operator' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Operators</SelectLabel>
                        <SelectItem value='equals'>Equals</SelectItem>
                        <SelectItem value='in'>Contains any of</SelectItem>
                        <SelectItem value='word_in'>
                          Contains any of the words within
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                {messageOperator === 'equals' ? (
                  <div className='space-y-2'>
                    <span className='text-sm font-medium'>Message Text</span>
                    <Input
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      placeholder='Enter exact message text to match'
                    />
                  </div>
                ) : messageOperator === 'word_in' ? (
                  <div className='space-y-2'>
                    <span className='text-sm font-medium'>Words</span>
                    <div className='flex gap-2'>
                      <Input
                        value={tempWord}
                        onChange={(e) => setTempWord(e.target.value)}
                        onKeyPress={handleWordKeyPress}
                        placeholder='Add word and press Enter'
                      />
                      <Button
                        type='button'
                        onClick={addWordToList}
                        variant='outline'
                      >
                        Add
                      </Button>
                    </div>
                    {messageWords.length > 0 && (
                      <div className='mt-2 flex flex-wrap gap-2'>
                        {messageWords.map((word, index) => (
                          <div
                            key={index}
                            className='flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1'
                          >
                            <span className='text-sm text-foreground'>
                              {word}
                            </span>
                            <Button
                              type='button'
                              onClick={() => removeWordFromList(index)}
                              variant='ghost'
                              className='h-5 w-5 p-0'
                            >
                              <X className='h-3 w-3' />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className='space-y-2'>
                    <span className='text-sm font-medium'>Message Texts</span>
                    <div className='flex gap-2'>
                      <Input
                        value={tempText}
                        onChange={(e) => setTempText(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder='Add message text and press Enter'
                      />
                      <Button
                        type='button'
                        onClick={addTextToList}
                        variant='outline'
                      >
                        Add
                      </Button>
                    </div>
                    {messageTexts.length > 0 && (
                      <div className='mt-2 flex flex-wrap gap-2'>
                        {messageTexts.map((text, index) => (
                          <div
                            key={index}
                            className='flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1'
                          >
                            <span className='text-sm text-foreground'>
                              {text}
                            </span>
                            <Button
                              type='button'
                              onClick={() => removeTextFromList(index)}
                              variant='ghost'
                              className='h-5 w-5 p-0'
                            >
                              <X className='h-3 w-3' />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {currentTriggerType === 'comment_text' && (
              <>
                <div className='space-y-2'>
                  <span className='text-sm font-medium'>Select Operator</span>
                  <Select
                    value={commentOperator}
                    onValueChange={setCommentOperator}
                  >
                    <SelectTrigger className='w-full'>
                      <SelectValue placeholder='Select an operator' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Operators</SelectLabel>
                        <SelectItem value='equals'>Equals</SelectItem>
                        <SelectItem value='in'>Contains any of</SelectItem>
                        <SelectItem value='word_in'>
                          Contains any of the words within
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                {commentOperator === 'equals' ? (
                  <div className='space-y-2'>
                    <span className='text-sm font-medium'>Comment Text</span>
                    <Input
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder='Enter exact comment text to match'
                    />
                  </div>
                ) : commentOperator === 'word_in' ? (
                  <div className='space-y-2'>
                    <span className='text-sm font-medium'>Words</span>
                    <div className='flex gap-2'>
                      <Input
                        value={tempCommentWord}
                        onChange={(e) => setTempCommentWord(e.target.value)}
                        onKeyPress={handleCommentWordKeyPress}
                        placeholder='Add word and press Enter'
                      />
                      <Button
                        type='button'
                        onClick={addCommentWordToList}
                        variant='outline'
                      >
                        Add
                      </Button>
                    </div>
                    {commentWords.length > 0 && (
                      <div className='mt-2 flex flex-wrap gap-2'>
                        {commentWords.map((word, index) => (
                          <div
                            key={index}
                            className='flex items-center gap-1 rounded-md bg-blue-500/10 px-2 py-1'
                          >
                            <span className='text-sm text-foreground'>
                              {word}
                            </span>
                            <Button
                              type='button'
                              onClick={() => removeCommentWordFromList(index)}
                              variant='ghost'
                              className='h-5 w-5 p-0'
                            >
                              <X className='h-3 w-3' />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className='space-y-2'>
                    <span className='text-sm font-medium'>Comment Texts</span>
                    <div className='flex gap-2'>
                      <Input
                        value={tempCommentText}
                        onChange={(e) => setTempCommentText(e.target.value)}
                        onKeyPress={handleCommentKeyPress}
                        placeholder='Add comment text and press Enter'
                      />
                      <Button
                        type='button'
                        onClick={addCommentTextToList}
                        variant='outline'
                      >
                        Add
                      </Button>
                    </div>
                    {commentTexts.length > 0 && (
                      <div className='mt-2 flex flex-wrap gap-2'>
                        {commentTexts.map((text, index) => (
                          <div
                            key={index}
                            className='flex items-center gap-1 rounded-md bg-blue-500/10 px-2 py-1'
                          >
                            <span className='text-sm text-foreground'>
                              {text}
                            </span>
                            <Button
                              type='button'
                              onClick={() => removeCommentTextFromList(index)}
                              variant='ghost'
                              className='h-5 w-5 p-0'
                            >
                              <X className='h-3 w-3' />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {currentTriggerType === 'contact_tag' && (
              <>
                <div className='space-y-2'>
                  <span className='text-sm font-medium'>Select Operator</span>
                  <Select value={tagOperator} onValueChange={setTagOperator}>
                    <SelectTrigger className='w-full'>
                      <SelectValue placeholder='Select an operator' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Operators</SelectLabel>
                        <SelectItem value='equals'>Equals</SelectItem>
                        <SelectItem value='in'>Contains any of</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                <div className='space-y-2'>
                  <span className='text-sm font-medium'>Select Tags</span>

                  {tagOperator === 'equals' ? (
                    // Single tag selector for 'equals' operator
                    <Select
                      value={
                        selectedTags.length > 0 ? selectedTags[0] : undefined
                      }
                      onValueChange={(value) => setSelectedTags([value])}
                    >
                      <SelectTrigger className='w-full'>
                        <SelectValue placeholder='Select a tag' />
                      </SelectTrigger>
                      <SelectContent>
                        {tags.length > 0 ? (
                          <SelectGroup>
                            <SelectLabel>Available Tags</SelectLabel>
                            {tags.map((tag) => (
                              <SelectItem key={tag._id} value={tag._id}>
                                <div className='flex items-center gap-2'>
                                  <div
                                    className='h-2 w-2 rounded-full'
                                    style={{
                                      backgroundColor: tag.colour || '#6E56CF',
                                    }}
                                  />
                                  {tag.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ) : (
                          <div className='px-2 py-4 text-center text-sm'>
                            No tags found
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  ) : (
                    // Multiple tag selector with checkboxes for 'in' operator
                    <div className='rounded-md border'>
                      <div className='border-b bg-secondary/10 p-2 text-sm font-medium'>
                        Select multiple tags
                      </div>
                      <ScrollArea className='h-[200px] p-2'>
                        {tags.length > 0 ? (
                          <div className='space-y-2'>
                            {tags.map((tag) => {
                              const isSelected = selectedTags.includes(tag._id);
                              return (
                                <div
                                  key={tag._id}
                                  className='flex cursor-pointer items-center gap-2 rounded-md p-1.5 hover:bg-secondary/10'
                                  onClick={() => {
                                    if (isSelected) {
                                      setSelectedTags(
                                        selectedTags.filter(
                                          (id) => id !== tag._id
                                        )
                                      );
                                    } else {
                                      setSelectedTags([
                                        ...selectedTags,
                                        tag._id,
                                      ]);
                                    }
                                  }}
                                >
                                  <div
                                    className={`flex h-4 w-4 items-center justify-center rounded border ${
                                      isSelected
                                        ? 'border-primary bg-primary'
                                        : 'border-input'
                                    }`}
                                  >
                                    {isSelected && (
                                      <Check className='h-3 w-3 text-primary-foreground' />
                                    )}
                                  </div>

                                  <div className='flex items-center gap-2'>
                                    <div
                                      className='h-2 w-2 rounded-full'
                                      style={{
                                        backgroundColor:
                                          tag.colour || '#6E56CF',
                                      }}
                                    />
                                    <span className='text-sm'>{tag.name}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className='py-4 text-center text-sm'>
                            No tags found
                          </div>
                        )}
                      </ScrollArea>
                      {selectedTags.length > 0 && (
                        <div className='flex items-center justify-between border-t bg-secondary/10 p-2'>
                          <span className='text-xs text-muted-foreground'>
                            {selectedTags.length} tag
                            {selectedTags.length !== 1 ? 's' : ''} selected
                          </span>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='h-7 text-xs'
                            onClick={() => setSelectedTags([])}
                          >
                            Clear all
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
            {currentTriggerType === 'schedule' && (
              <>
                {/* Interval Configuration */}
                <div className='space-y-4'>
                  <div className='space-y-2'>
                    <span className='text-sm font-medium'>
                      Schedule Interval
                    </span>
                    <div className='flex gap-2'>
                      <Input
                        type='number'
                        value={scheduleValue}
                        onChange={(e) => {
                          setScheduleValue(e.target.value);
                        }}
                        placeholder='Enter interval value'
                        className='flex-1'
                      />
                      <Select
                        value={scheduleUnit}
                        onValueChange={setScheduleUnit}
                      >
                        <SelectTrigger className='w-32'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='minutes'>Minutes</SelectItem>
                          <SelectItem value='hours'>Hours</SelectItem>
                          <SelectItem value='days'>Days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Conditions Section */}
                  <div className='space-y-4'>
                    <div className='flex items-center justify-between'>
                      <span className='text-sm font-medium'>Conditions</span>
                      <span className='text-xs text-muted-foreground'>
                        {scheduleConditions.length} condition
                        {scheduleConditions.length !== 1 ? 's' : ''} added
                      </span>
                    </div>

                    {/* Add New Condition Form */}
                    <div className='space-y-3 rounded-md border p-4'>
                      <span className='text-sm font-medium text-muted-foreground'>
                        Add New Condition
                      </span>

                      {/* Field Selection */}
                      <div className='space-y-2'>
                        <span className='text-xs font-medium'>Field</span>
                        <Select
                          value={selectedConditionField}
                          onValueChange={setSelectedConditionField}
                        >
                          <SelectTrigger className='w-full'>
                            <SelectValue placeholder='Select condition field' />
                          </SelectTrigger>
                          <SelectContent>
                            {conditionFieldOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                <div>
                                  <div className='font-medium'>
                                    {option.label}
                                  </div>
                                  <div className='text-xs text-muted-foreground'>
                                    {option.description}
                                  </div>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Operator Selection */}
                      {selectedConditionField && (
                        <div className='space-y-2'>
                          <span className='text-xs font-medium'>Operator</span>
                          <Select
                            value={selectedConditionOperator}
                            onValueChange={setSelectedConditionOperator}
                          >
                            <SelectTrigger className='w-full'>
                              <SelectValue placeholder='Select operator' />
                            </SelectTrigger>
                            <SelectContent>
                              {selectedConditionField === 'contact_tag' && (
                                <>
                                  <SelectItem value='equals'>Equals</SelectItem>
                                  <SelectItem value='in'>
                                    Contains any of
                                  </SelectItem>
                                </>
                              )}
                              {selectedConditionField ===
                                'contact.latest_message_received_timestamp' && (
                                <>
                                  <SelectItem value='compare_within_timestamp'>
                                    Greater than
                                  </SelectItem>
                                </>
                              )}
                              {selectedConditionField ===
                                'contact.latest_message_sent_timestamp' && (
                                <>
                                  <SelectItem value='compare_within_timestamp'>
                                    Greater than
                                  </SelectItem>
                                </>
                              )}
                              {selectedConditionField ===
                                'contact.latest_message_direction' && (
                                <>
                                  <SelectItem value='equals'>Equals</SelectItem>
                                </>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Value Configuration based on field */}
                      {selectedConditionField && selectedConditionOperator && (
                        <div className='space-y-2'>
                          <span className='text-xs font-medium'>Value</span>

                          {/* Contact Tag Value */}
                          {selectedConditionField === 'contact_tag' && (
                            <div className='space-y-2'>
                              {selectedConditionOperator === 'equals' ? (
                                <Select
                                  value={
                                    conditionTagIds.length > 0
                                      ? conditionTagIds[0]
                                      : undefined
                                  }
                                  onValueChange={(value) =>
                                    setConditionTagIds([value])
                                  }
                                >
                                  <SelectTrigger className='w-full'>
                                    <SelectValue placeholder='Select a tag' />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {tags.length > 0 ? (
                                      <SelectGroup>
                                        <SelectLabel>
                                          Available Tags
                                        </SelectLabel>
                                        {tags.map((tag) => (
                                          <SelectItem
                                            key={tag._id}
                                            value={tag._id}
                                          >
                                            <div className='flex items-center gap-2'>
                                              <div
                                                className='h-2 w-2 rounded-full'
                                                style={{
                                                  backgroundColor:
                                                    tag.colour || '#6E56CF',
                                                }}
                                              />
                                              {tag.name}
                                            </div>
                                          </SelectItem>
                                        ))}
                                      </SelectGroup>
                                    ) : (
                                      <div className='px-2 py-4 text-center text-sm'>
                                        No tags found
                                      </div>
                                    )}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <div className='rounded-md border'>
                                  <div className='border-b bg-secondary/10 p-2 text-sm font-medium'>
                                    Select multiple tags
                                  </div>
                                  <ScrollArea className='h-[200px] p-2'>
                                    {tags.length > 0 ? (
                                      <div className='space-y-2'>
                                        {tags.map((tag) => {
                                          const isSelected =
                                            conditionTagIds.includes(tag._id);
                                          return (
                                            <div
                                              key={tag._id}
                                              className='flex cursor-pointer items-center gap-2 rounded-md p-1.5 hover:bg-secondary/10'
                                              onClick={() => {
                                                if (isSelected) {
                                                  setConditionTagIds(
                                                    conditionTagIds.filter(
                                                      (id) => id !== tag._id
                                                    )
                                                  );
                                                } else {
                                                  setConditionTagIds([
                                                    ...conditionTagIds,
                                                    tag._id,
                                                  ]);
                                                }
                                              }}
                                            >
                                              <div
                                                className={`flex h-4 w-4 items-center justify-center rounded border ${
                                                  isSelected
                                                    ? 'border-primary bg-primary'
                                                    : 'border-input'
                                                }`}
                                              >
                                                {isSelected && (
                                                  <Check className='h-3 w-3 text-primary-foreground' />
                                                )}
                                              </div>

                                              <div className='flex items-center gap-2'>
                                                <div
                                                  className='h-2 w-2 rounded-full'
                                                  style={{
                                                    backgroundColor:
                                                      tag.colour || '#6E56CF',
                                                  }}
                                                />
                                                <span className='text-sm'>
                                                  {tag.name}
                                                </span>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <div className='py-4 text-center text-sm'>
                                        No tags found
                                      </div>
                                    )}
                                  </ScrollArea>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Received Timestamp Value */}
                          {selectedConditionField ===
                            'contact.latest_message_received_timestamp' && (
                            <div className='space-y-2'>
                              <div className='flex gap-2'>
                                <Select
                                  value={conditionTimestampOperator}
                                  onValueChange={setConditionTimestampOperator}
                                >
                                  <SelectTrigger className='w-24'>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value='gt'>
                                      Greater than
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                                <Input
                                  type='number'
                                  min='1'
                                  value={conditionTimestampValue}
                                  onChange={(e) => {
                                    setConditionTimestampValue(e.target.value);
                                  }}
                                  placeholder='Value'
                                  className='flex-1'
                                />
                                <Select
                                  value={conditionTimestampUnit}
                                  onValueChange={setConditionTimestampUnit}
                                >
                                  <SelectTrigger className='w-24'>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value='seconds'>
                                      Seconds
                                    </SelectItem>
                                    <SelectItem value='minutes'>
                                      Minutes
                                    </SelectItem>
                                    <SelectItem value='hours'>Hours</SelectItem>
                                    <SelectItem value='days'>Days</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          )}

                          {/* Sent Timestamp Value */}
                          {selectedConditionField ===
                            'contact.latest_message_sent_timestamp' && (
                            <div className='space-y-2'>
                              <div className='flex gap-2'>
                                <Select
                                  value={conditionTimestampOperator}
                                  onValueChange={setConditionTimestampOperator}
                                >
                                  <SelectTrigger className='w-24'>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value='gt'>
                                      Greater than
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                                <Input
                                  type='number'
                                  min='1'
                                  value={conditionTimestampValue}
                                  onChange={(e) => {
                                    setConditionTimestampValue(e.target.value);
                                  }}
                                  placeholder='Value'
                                  className='flex-1'
                                />
                                <Select
                                  value={conditionTimestampUnit}
                                  onValueChange={setConditionTimestampUnit}
                                >
                                  <SelectTrigger className='w-24'>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value='seconds'>
                                      Seconds
                                    </SelectItem>
                                    <SelectItem value='minutes'>
                                      Minutes
                                    </SelectItem>
                                    <SelectItem value='hours'>Hours</SelectItem>
                                    <SelectItem value='days'>Days</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          )}

                          {/* Message Direction Value */}
                          {selectedConditionField ===
                            'contact.latest_message_direction' && (
                            <Select
                              value={conditionMessageDirection}
                              onValueChange={setConditionMessageDirection}
                            >
                              <SelectTrigger className='w-full'>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value='sent'>Sent</SelectItem>
                                <SelectItem value='received'>
                                  Received
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      )}

                      {/* Add Condition Button */}
                      {selectedConditionField && selectedConditionOperator && (
                        <Button
                          type='button'
                          onClick={addCondition}
                          variant='outline'
                          className='w-full'
                        >
                          Add Condition
                        </Button>
                      )}
                    </div>

                    {/* Display Existing Conditions */}
                    {scheduleConditions.length > 0 && (
                      <div className='space-y-2'>
                        {scheduleConditions.map((condition, index) => (
                          <div
                            key={index}
                            className='flex items-center justify-between rounded-md border bg-secondary/10 p-3'
                          >
                            <div className='flex-1'>
                              <div className='text-sm font-medium'>
                                {
                                  conditionFieldOptions.find(
                                    (f) => f.value === condition.field
                                  )?.label
                                }
                              </div>
                              <div className='text-xs text-muted-foreground'>
                                {condition.operator === 'equals' &&
                                  condition.field === 'contact_tag' &&
                                  `Equals: ${
                                    tags.find(
                                      (t) => t._id === condition.value[0]
                                    )?.name
                                  }`}
                                {condition.operator === 'in' &&
                                  condition.field === 'contact_tag' &&
                                  `Contains any of: ${condition.value
                                    .map(
                                      (id) =>
                                        tags.find((t) => t._id === id)?.name
                                    )
                                    .join(', ')}`}
                                {condition.field ===
                                  'contact.latest_message_received_timestamp' &&
                                  `Greater than ${condition.value.unit_value} ${condition.value.unit}`}
                                {condition.field ===
                                  'contact.latest_message_sent_timestamp' &&
                                  `Greater than ${condition.value.unit_value} ${condition.value.unit}`}
                                {condition.field ===
                                  'contact.latest_message_direction' &&
                                  `Equals: ${condition.value}`}
                              </div>
                            </div>
                            <Button
                              type='button'
                              onClick={() => removeCondition(index)}
                              variant='ghost'
                              size='sm'
                              className='h-7 w-7 p-0'
                            >
                              <X className='h-3 w-3' />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
            {currentTriggerType === 'linkedin_post_comment' && (
              <>
                {/* Post Selection */}
                <div className='space-y-4'>
                  <div className='space-y-2'>
                    <div className='flex items-center justify-between'>
                      <span className='text-sm font-medium'>Select Posts</span>
                      {linkedinSelectedPostIds.length > 0 && (
                        <span className='text-xs text-muted-foreground'>
                          {linkedinSelectedPostIds.length} post
                          {linkedinSelectedPostIds.length !== 1 ? 's' : ''}{' '}
                          selected
                        </span>
                      )}
                    </div>

                    {/* Search and Refresh */}
                    <div className='flex gap-2'>
                      <div className='relative flex-1'>
                        <Search className='absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                        <Input
                          value={linkedinSearchInput}
                          onChange={(e) =>
                            setLinkedinSearchInput(e.target.value)
                          }
                          onKeyPress={handleLinkedinSearchKeyPress}
                          placeholder='Search posts...'
                          className='pl-8'
                        />
                      </div>
                      <Button
                        type='button'
                        variant='outline'
                        size='icon'
                        onClick={handleLinkedinSearch}
                        disabled={linkedinPostsLoading}
                      >
                        <Search className='h-4 w-4' />
                      </Button>
                      <Button
                        type='button'
                        variant='outline'
                        size='icon'
                        onClick={handleLinkedinRefresh}
                        disabled={linkedinPostsLoading}
                        title='Refresh posts'
                      >
                        <RefreshCw
                          className={`h-4 w-4 ${
                            linkedinPostsLoading ? 'animate-spin' : ''
                          }`}
                        />
                      </Button>
                    </div>

                    {linkedinPostsLoading ? (
                      <div className='flex items-center justify-center py-4'>
                        <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
                        <span className='ml-2 text-sm text-muted-foreground'>
                          Loading posts...
                        </span>
                      </div>
                    ) : linkedinPosts.length > 0 ? (
                      <div className='rounded-md border'>
                        <ScrollArea className='h-[250px]'>
                          <div className='space-y-1 p-2'>
                            {linkedinPosts.map((post) => {
                              const isSelected =
                                linkedinSelectedPostIds.includes(post.post_id);
                              return (
                                <div
                                  key={post.id}
                                  className={`max-w-[320px] cursor-pointer rounded-md border p-3 transition-colors ${
                                    isSelected
                                      ? 'border-primary bg-primary/10'
                                      : 'hover:bg-muted'
                                  }`}
                                  onClick={() =>
                                    toggleLinkedinPostSelection(post.post_id)
                                  }
                                >
                                  <div className='flex items-start gap-3'>
                                    <div
                                      className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                        isSelected
                                          ? 'border-primary bg-primary'
                                          : 'border-input'
                                      }`}
                                    >
                                      {isSelected && (
                                        <Check className='h-3 w-3 text-primary-foreground' />
                                      )}
                                    </div>
                                    <div className='min-w-0 flex-1'>
                                      <p className='truncate text-sm font-medium'>
                                        {post.title}
                                      </p>
                                      <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                                        <span>
                                          {post.parsed_datetime
                                            ? new Date(
                                                post.parsed_datetime
                                              ).toLocaleString(undefined, {
                                                dateStyle: 'medium',
                                                timeStyle: 'short',
                                              })
                                            : new Date(
                                                post.created_at
                                              ).toLocaleString(undefined, {
                                                dateStyle: 'medium',
                                                timeStyle: 'short',
                                              })}
                                        </span>
                                        {post.attachments &&
                                          post.attachments.length > 0 && (
                                            <>
                                              <span>•</span>
                                              <span>
                                                {post.attachments.length}{' '}
                                                attachment
                                                {post.attachments.length !== 1
                                                  ? 's'
                                                  : ''}
                                              </span>
                                            </>
                                          )}
                                      </div>
                                    </div>
                                    {post.share_url && (
                                      <Button
                                        type='button'
                                        variant='ghost'
                                        size='icon'
                                        className='h-7 w-7 shrink-0'
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          window.open(post.share_url, '_blank');
                                        }}
                                        title='Open post in LinkedIn'
                                      >
                                        <ExternalLink className='h-3.5 w-3.5' />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </ScrollArea>
                        {linkedinSelectedPostIds.length > 0 && (
                          <div className='flex items-center justify-between border-t bg-secondary/10 p-2'>
                            <span className='text-xs text-muted-foreground'>
                              {linkedinSelectedPostIds.length} post
                              {linkedinSelectedPostIds.length !== 1
                                ? 's'
                                : ''}{' '}
                              selected
                            </span>
                            <Button
                              variant='ghost'
                              size='sm'
                              className='h-7 text-xs'
                              onClick={() => setLinkedinSelectedPostIds([])}
                            >
                              Clear all
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className='py-4 text-center text-sm text-muted-foreground'>
                        No posts found
                      </div>
                    )}
                  </div>
                </div>

                {/* Operator Selection */}
                <div className='space-y-2'>
                  <span className='text-sm font-medium'>Match Condition</span>
                  <Select
                    value={linkedinOperator}
                    onValueChange={setLinkedinOperator}
                  >
                    <SelectTrigger className='w-full'>
                      <SelectValue placeholder='Select a condition' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Conditions</SelectLabel>
                        <SelectItem value='equals'>Equals</SelectItem>
                        <SelectItem value='in'>Contains any of</SelectItem>
                        <SelectItem value='word_in'>
                          Contains any of the words within
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                {/* Value Input based on Operator */}
                {linkedinOperator === 'equals' && (
                  <div className='space-y-2'>
                    <span className='text-sm font-medium'>Comment Text</span>
                    <Input
                      value={linkedinCommentText}
                      onChange={(e) => setLinkedinCommentText(e.target.value)}
                      placeholder='Enter exact comment text to match'
                    />
                  </div>
                )}

                {linkedinOperator === 'word_in' && (
                  <div className='space-y-2'>
                    <span className='text-sm font-medium'>Keywords</span>
                    <div className='flex gap-2'>
                      <Input
                        value={tempLinkedinWord}
                        onChange={(e) => setTempLinkedinWord(e.target.value)}
                        onKeyPress={handleLinkedinWordKeyPress}
                        placeholder='Add keyword and press Enter'
                      />
                      <Button
                        type='button'
                        onClick={addLinkedinWordToList}
                        variant='outline'
                      >
                        Add
                      </Button>
                    </div>
                    {linkedinCommentWords.length > 0 && (
                      <div className='mt-2 flex flex-wrap gap-2'>
                        {linkedinCommentWords.map((word, index) => (
                          <div
                            key={index}
                            className='flex items-center gap-1 rounded-md bg-blue-600/10 px-2 py-1'
                          >
                            <span className='text-sm text-foreground'>
                              {word}
                            </span>
                            <Button
                              type='button'
                              onClick={() => removeLinkedinWordFromList(index)}
                              variant='ghost'
                              className='h-5 w-5 p-0'
                            >
                              <X className='h-3 w-3' />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {linkedinOperator === 'in' && (
                  <div className='space-y-2'>
                    <span className='text-sm font-medium'>Comment Texts</span>
                    <div className='flex gap-2'>
                      <Input
                        value={tempLinkedinText}
                        onChange={(e) => setTempLinkedinText(e.target.value)}
                        onKeyPress={handleLinkedinTextKeyPress}
                        placeholder='Add text and press Enter'
                      />
                      <Button
                        type='button'
                        onClick={addLinkedinTextToList}
                        variant='outline'
                      >
                        Add
                      </Button>
                    </div>
                    {linkedinCommentTexts.length > 0 && (
                      <div className='mt-2 flex flex-wrap gap-2'>
                        {linkedinCommentTexts.map((text, index) => (
                          <div
                            key={index}
                            className='flex items-center gap-1 rounded-md bg-blue-600/10 px-2 py-1'
                          >
                            <span className='text-sm text-foreground'>
                              {text}
                            </span>
                            <Button
                              type='button'
                              onClick={() => removeLinkedinTextFromList(index)}
                              variant='ghost'
                              className='h-5 w-5 p-0'
                            >
                              <X className='h-3 w-3' />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <SheetFooter className='mt-6'>
            <Button
              type='button'
              variant='outline'
              onClick={() => setIsConfigSheetOpen(false)}
            >
              Cancel
            </Button>
            <Button type='button' onClick={handleSaveConfig}>
              Save
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Dialog open={isTriggerModalOpen} onOpenChange={setIsTriggerModalOpen}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Select a Trigger</DialogTitle>
          </DialogHeader>
          <div className='grid grid-cols-1 gap-4 py-4'>
            {triggerOptions.map((option) => {
              const channels = channelCapabilities.triggers[option.type] || [];
              return (
                <Button
                  key={option.type}
                  onClick={() => handleTriggerTypeSelection(option.type)}
                  variant='outline'
                  className='flex h-auto flex-col items-start justify-start gap-2 p-4 hover:bg-accent/20'
                >
                  <div className='flex w-full items-center gap-2'>
                    <option.icon className={`h-5 w-5 ${option.iconClass}`} />
                    <span className='font-medium'>{option.label}</span>
                  </div>
                  <span className='text-xs text-muted-foreground'>
                    {option.description}
                  </span>
                  <div className='flex flex-wrap gap-1'>
                    {channels.map((ch) => {
                      const info = getChannelInfo(ch);
                      const IconComponent = info.icon;
                      return (
                        <Badge
                          key={ch}
                          variant='secondary'
                          className='flex items-center px-1.5 py-0.5'
                        >
                          {IconComponent && (
                            <IconComponent
                              className={`h-3 w-3 ${info.color}`}
                            />
                          )}
                        </Badge>
                      );
                    })}
                  </div>
                </Button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TriggerConfiguration;
